import { ProductCreation, ProductCreationWithErrors, productUnits } from '@Arti-zen/package-backoffice'
import { Button, Dialog } from '@mui/material'
import InputTextControl from 'components/forms/InputText.control'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import { yupResolver } from '@hookform/resolvers/yup'
import SelectMuiControl from 'components/forms/SelectMui.control'
import { ProductBaseSchema } from './ProductSchema'

type TProps = {
  product: ProductCreation | ProductCreationWithErrors | null
  onClose: () => void
  correctProduct: (corrections: Partial<ProductCreation>) => void
}

export default function FormProductError({ product, onClose, correctProduct }: TProps) {
  const {
    control,
    watch,
    reset,
    trigger,
    handleSubmit,
    setValue,
    formState: { errors },
    // @ts-expect-error
  } = useForm({ defaultValues: product, resolver: yupResolver(ProductBaseSchema), mode: 'onChange' })

  const coef = watch('coef') ? Number(watch('coef')) : 0
  const price = watch('price') ? Number(watch('price')) : 0
  const netPrice = watch('netPrice') ? Number(watch('netPrice')) : 0

  useEffect(() => {
    trigger(['price', 'netPrice', 'coef'])
  }, [price, netPrice, coef, trigger])

  useEffect(() => {
    reset(product)
    trigger()
  }, [product])

  const unitOptions = useMemo(
    () => [
      ...productUnits.map((u) => {
        return { label: u, value: u }
      }),
      { label: 'Aucun', value: null },
    ],
    [productUnits]
  )

  const vatOptions = [
    ,
    ...[0, 5.5, 10, 20, 2.1, 8.5].map((v) => {
      return { value: v, label: `${v}%` }
    }),
    { label: 'Aucun', value: null },
  ]

  const submit = (data) => {
    correctProduct(data)
    onClose()
  }

  const onError = (err: any) => {
    console.error('err : ', err)
  }

  return (
    <Dialog
      open={!!product}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '50%',
        },
      }}
    >
      <Container>
        <FormContainer>
          <div style={{ gridColumn: 'span 2' }}>
            <InputTextControl
              control={control}
              name={'description'}
              label="Désignation* (description)"
              error={errors?.['description']}
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <InputTextControl
              control={control}
              name={'allDescription'}
              label="Description (allDescription)"
              error={errors?.['allDescription']}
              multiline
              minRows={2}
            />
          </div>
          <div>
            <InputTextControl
              control={control}
              name={'netPrice'}
              label="Coût HT (netPrice)"
              endAdornment={<>€</>}
              error={errors?.['netPrice']}
            />

            {!!coef && !!price && netPrice !== Math.round((price / (1 + coef / 100)) * 100) / 100 && (
              <CalculButton
                onClick={() =>
                  setValue('netPrice', String(Math.round((price / (1 + coef / 100)) * 100) / 100), {
                    shouldValidate: true,
                  })
                }
              >
                Calculer coût facturé
              </CalculButton>
            )}
          </div>
          <div>
            <InputTextControl
              control={control}
              name={'coef'}
              label="Marge (coef)"
              endAdornment={<>%</>}
              error={errors?.['coef']}
            />
            {!!price && !!netPrice && coef !== Math.round(((price - netPrice) / netPrice) * 10000) / 100 && (
              <CalculButton
                onClick={() => {
                  setValue('coef', String(Math.round(((price - netPrice) / netPrice) * 10000) / 100), {
                    shouldValidate: true,
                  })
                }}
              >
                Calculer marge
              </CalculButton>
            )}
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <InputTextControl
              control={control}
              name={'price'}
              label="Prix HT facturé* (price)"
              endAdornment={<>€</>}
              error={errors?.['price']}
            />
          </div>
          <SelectMuiControl
            control={control}
            name={'vat'}
            label="TVA (vat)"
            error={errors?.['vat']}
            items={vatOptions}
          />
          <SelectMuiControl
            control={control}
            name={'unit'}
            label="Unité (unit)"
            fullWidth
            error={errors?.['unit']}
            items={unitOptions}
          />
        </FormContainer>
        <Button
          onClick={handleSubmit(submit, onError)}
          style={{
            display: 'flex',
            alignSelf: 'center',
            backgroundColor: Theme.colors.blueArtizen,
            color: 'white',
            width: '100%',
            marginTop: 40,
          }}
        >
          Valider
        </Button>
      </Container>
    </Dialog>
  )
}

const Container = styled.div`
  padding: 30px 20px;
`

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  align-items: start;
`

const CalculButton = styled.p`
  width: 100%;
  text-align: center;
  color: ${Theme.colors.blueArtizen};
  font-weight: bold;
  margin: 0;
  padding-top: 5px;
  cursor: pointer;
`
